import { useState, useId } from 'react';
import { func, instanceOf, string, number, bool } from 'prop-types';
import iconAddGrey from '@assets/img/add_grey.svg';
import './FileDropzone.scss';

const FileDropzone = ({ 
  onFileSelect, 
  selectedFile = null,
  placeholder = 'Sleep een bestand hierheen of klik om te selecteren',
  accept,
  maxSize,
  disabled = false,
}) => {
  const [dragCounter, setDragCounter] = useState(0);
  const inputId = useId();

  const isValidFileType = (file) => {
    if (!accept) return true;
    const acceptedTypes = accept.split(',').map(type => type.trim());
    return acceptedTypes.some(type => {
      if (type.startsWith('.')) {
        return file.name.toLowerCase().endsWith(type.toLowerCase());
      }
      if (type.endsWith('/*')) {
        const mimePrefix = type.slice(0, -2);
        return file.type.startsWith(mimePrefix);
      }
      return file.type === type;
    });
  };

  const isValidFileSize = (file) => {
    if (!maxSize) return true;
    return file.size <= maxSize;
  };

  const handleFileSelect = (file) => {
    if (!file || disabled) return;
    
    if (!isValidFileType(file) || !isValidFileSize(file)) {
      return;
    }
    
    onFileSelect?.(file);
  };

  const handleDragEnter = (e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => prev + 1);
  };

  const handleDragLeave = (e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => prev - 1);
  };

  const handleDragOver = (e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(0);
    
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleClick = () => {
    if (disabled) return;
    document.getElementById(inputId)?.click();
  };

  const isFileTooLarge = maxSize && selectedFile?.size > maxSize;
  const isInvalidFileType = accept && selectedFile && !isValidFileType(selectedFile);
  const fileName = selectedFile?.name || '';

  return (
    <div className="file-dropzone-wrapper">
      <div 
        className={`file-dropzone ${dragCounter > 0 ? 'dragging' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={handleClick}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="file-dropzone-icon">
          <img src={iconAddGrey} height={20} alt="Add file" />
        </div>
        <div className="file-dropzone-content">
          {fileName ? (
            <span className="file-name">{fileName}</span>
          ) : (
            <span className="placeholder-text">{placeholder}</span>
          )}
        </div>
      </div>
      <input
        id={inputId}
        type="file"
        className="file-dropzone-input"
        accept={accept}
        onChange={(event) => handleFileSelect(event.target.files[0] || null)}
        disabled={disabled}
      />
      {isFileTooLarge && (
        <p className="file-dropzone-error">
          De maximum grootte voor bestanden is {Math.round(maxSize / (1024 * 1024))}MB
        </p>
      )}
      {isInvalidFileType && (
        <p className="file-dropzone-error">
          Dit bestandstype is niet toegestaan
        </p>
      )}
    </div>
  );
};

FileDropzone.propTypes = {
  onFileSelect: func,
  selectedFile: instanceOf(File),
  placeholder: string,
  accept: string,
  maxSize: number,
  disabled: bool,
};

export default FileDropzone;
